import React, { useEffect, useMemo, useState } from 'react';
import {
  DataGrid,
  GridColDef,
  CellTypes,
  Button,
  Icon,
} from '@fashionnovateam/nova-ui-toolkit';
import {
  GridPaginationModel,
  GridSortModel,
} from '@fashionnovateam/nova-ui-toolkit/dist/molecules/DataGrid/utils';
import moment from 'moment/moment';
import { Link } from 'react-router-dom';
import {
  useGetVendorsApplications,
  useRefreshTokenVendorApplication,
} from '../../Lib/Hooks/vendor-application.hooks';
import { InviteToken } from '../../Lib/Api/vendor';
import determineApplicationStage from './utils/determineApplicationStage';
import determineApplicationStatus from './utils/determineApplicationStatus';
import Roles, {
  Actions,
  hasAccessTo,
  hasPermissionTo,
  RolesType,
} from '../../Lib/Constants/roles';
import {
  useArchiveApplication,
  useUnArchiveApplication,
} from '../../Lib/Hooks/vendor-onboarding.hooks';

type VendorApplianceRow = {
  id: number;
  vendor_name: CellTypes.DataGridCellCompanyParams;
  contact_email: CellTypes.DataGridCellEmailParams;
  vendor_type: CellTypes.DataGridCellBadgeParams;
  stage: CellTypes.DataGridCellStringParams;
  status: CellTypes.DataGridCellStringParams;
  updated_at: CellTypes.DataGridCellDateTimeParams;
  token_status: CellTypes.DataGridCellBadgeParams;
  view: CellTypes.DataGridCellCustomParams;
  refresh_token: CellTypes.DataGridCellButtonParams | null;
  archive_buttons: CellTypes.DataGridCellCustomParams | null;
};

const columns: GridColDef[] = [
  {
    field: 'vendor_name',
    headerName: 'Vendor Name',
    render: 'company',
    flex: 1,
    sortable: false,
    minWidth: 150,
    disableColumnMenu: true,
  },
  {
    field: 'contact_email',
    headerName: 'Contact',
    render: 'email',
    flex: 1,
    sortable: false,
    minWidth: 250,
    disableColumnMenu: true,
  },
  {
    field: 'vendor_type',
    headerName: 'Vendor Type',
    render: 'badge',
    flex: 1,
    sortable: false,
    minWidth: 150,
    disableColumnMenu: true,
  },
  {
    field: 'stage',
    headerName: 'Stage',
    render: 'string',
    display: 'flex',
    flex: 1,
    sortable: false,
    disableColumnMenu: true,
  },
  {
    field: 'status',
    headerName: 'Status',
    render: 'string',
    display: 'flex',
    flex: 1,
    sortable: false,
    minWidth: 150,
    disableColumnMenu: true,
  },
  {
    field: 'updated_at',
    headerName: 'Last Updated',
    render: 'dateTime',
    sortable: true,
    minWidth: 50,
    disableColumnMenu: true,
  },
  {
    field: 'token_status',
    headerName: 'Token Status',
    render: 'badge',
    flex: 1,
    sortable: false,
    minWidth: 200,
    disableColumnMenu: true,
  },
  {
    field: 'view',
    headerName: '',
    render: 'custom',
    minWidth: 75,
    sortable: false,
    disableColumnMenu: true,
  },
  {
    field: 'archive_buttons',
    headerName: '',
    render: 'custom',
    flex: 1,
    minWidth: 75,
    sortable: false,
    disableColumnMenu: true,
  },
  {
    field: 'refresh_token',
    headerName: '',
    render: 'button',
    flex: 1,
    minWidth: 125,
    sortable: false,
    disableColumnMenu: true,
  },
];

interface Props {
  filter: string;
  search: string;
  userRole: string;
}

function VendorApplianceDataGrid({ search, filter, userRole }: Props) {
  const { mutate: archiveApplication } = useArchiveApplication();
  const { mutate: unArchiveApplication } = useUnArchiveApplication();
  const { mutateAsync: refreshToken } = useRefreshTokenVendorApplication();
  const [loadingButtonIds, setLoadingButtonIds] = useState<string[]>([]);
  const [pagination, setPagination] = useState({
    pageSize: 15,
    page: 1,
  });

  const [sort, setSort] = useState({
    sortBy: 'updated_at',
    sortOrder: 'DESC',
  });

  const { isFetching, data, refetch } = useGetVendorsApplications({
    params: {
      limit: pagination.pageSize,
      page: pagination.page,
      search,
      filter,
      ...sort,
    },
  });

  const resetPagination = () => {
    setPagination({
      pageSize: 15,
      page: 1,
    });
  };

  useEffect(() => {
    resetPagination();
  }, [search, filter]);

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    setPagination((prevPagination) => ({
      ...prevPagination,
      page: model.page + 1,
    }));
  };

  const handleSortModelChange = (model: GridSortModel) => {
    setSort({
      sortBy: model[0]?.field,
      sortOrder: model[0]?.sort?.toUpperCase() || 'ASC',
    });
  };

  const getTokenStatus = (token: InviteToken) => {
    if (!token) {
      return '-';
    }
    const isExpired = moment(token.expires_at).isBefore(moment());
    if (token.status === 'EMAIL_SENT') {
      if (isExpired) {
        return 'Expired';
      }
      return `Expires at ${moment(token.expires_at).format('h:mma • MMM D, YYYY')}`;
    }
    return 'Consumed';
  };

  const transformedRows = useMemo(
    () =>
      data?.formattedData.map((application, id: number) => {
        const tokenStatus = getTokenStatus(application.inviteTokens[0]);
        const displayRefreshToken =
          tokenStatus !== '-' &&
          tokenStatus !== 'Consumed' &&
          hasPermissionTo(
            'vendor-applications',
            userRole as RolesType,
            Actions.UPDATE,
            'REFRESH',
          );
        const canArchive = [Roles.SPONSOR].some((role) => userRole === role);
        const displayArchiveButton =
          canArchive &&
          application.vendor_application_archived_at === null &&
          application.vendor_application_status !== 'Approved';
        const displayUnArchiveButton =
          canArchive &&
          application.vendor_application_archived_at !== null &&
          application.vendor_application_status !== 'Approved';
        const isTokenExpired = tokenStatus === 'Expired';
        return {
          id,
          vendor_name: {
            name: application.vendor_name,
          },
          contact_email: application.contact,
          vendor_type: {
            contrast: 'low',
            children: application.vendor_type,
            color: application.vendor_type.startsWith('New')
              ? 'green'
              : 'neutral',
          },
          stage: determineApplicationStage(application),
          status: determineApplicationStatus(application),
          updated_at: {
            activity: true,
            time: true,
            date: application.last_updated_at,
          },
          token_status: {
            contrast: 'low',
            children: (
              <>
                {isTokenExpired && <Icon.TimerOff />}
                {tokenStatus}
              </>
            ),
            color: isTokenExpired ? 'red' : 'neutral',
          },
          view: hasAccessTo(
            'vendor-applications/:vendorId/vendor-application-onboarding',
            userRole as RolesType,
          ) ? (
            <Link
              to={`/vendor-applications/${application.vendor_id}/vendor-application-onboarding`}
            >
              <Button size="sm" color="secondary">
                <Icon.ExternalLinkCircle />
                View
              </Button>
            </Link>
          ) : undefined,
          refresh_token: displayRefreshToken
            ? {
                loading: loadingButtonIds.includes(
                  application.inviteTokens[0]?.id.toString(),
                ),
                onClick: async () => {
                  const tokenId = application.inviteTokens[0]?.id.toString();
                  setLoadingButtonIds((prev) => [...prev, tokenId]);
                  try {
                    await refreshToken(tokenId);
                    await refetch();
                  } finally {
                    setLoadingButtonIds((prev) =>
                      prev.filter((i) => i !== tokenId),
                    );
                  }
                },
                size: 'sm',
                color: 'secondary',
                children: (
                  <>
                    <Icon.Refresh />
                    Refresh Token
                  </>
                ),
              }
            : null,
          archive_buttons: canArchive ? (
            <div className="flex h-full gap-2 items-center">
              {displayArchiveButton && (
                <Button
                  size="sm"
                  color="secondary"
                  loading={loadingButtonIds.includes(
                    application.inviteTokens[0]?.id.toString(),
                  )}
                  onClick={async (event) => {
                    const tokenId = application.inviteTokens[0]?.id.toString();
                    setLoadingButtonIds((prev) => [...prev, tokenId]);
                    try {
                      archiveApplication({ id: application.vendor_id });
                    } finally {
                      setLoadingButtonIds((prev) =>
                        prev.filter((i) => i !== tokenId),
                      );
                      event.currentTarget.blur();
                    }
                  }}
                >
                  Archive
                </Button>
              )}
              {displayUnArchiveButton && (
                <Button
                  size="sm"
                  color="secondary"
                  loading={loadingButtonIds.includes(
                    application.inviteTokens[0]?.id.toString(),
                  )}
                  onClick={async (event) => {
                    const tokenId = application.inviteTokens[0]?.id.toString();
                    setLoadingButtonIds((prev) => [...prev, tokenId]);
                    try {
                      unArchiveApplication({ id: application.vendor_id });
                    } finally {
                      setLoadingButtonIds((prev) =>
                        prev.filter((i) => i !== tokenId),
                      );
                      event.currentTarget.blur();
                    }
                  }}
                >
                  Unarchive
                </Button>
              )}
            </div>
          ) : (
            ''
          ),
        } satisfies VendorApplianceRow;
      }),
    [data?.formattedData, loadingButtonIds],
  );

  return (
    <DataGrid
      autosizeOnMount
      hideFilterButton
      hideGroupButton
      columns={columns.filter((col) => {
        const displaySponsorColumns = [Roles.SPONSOR].some(
          (role) => userRole === role,
        );
        return displaySponsorColumns ? true : col.field !== 'archive_buttons';
      })}
      loading={isFetching}
      rows={transformedRows ?? []}
      initialState={{
        pinnedColumns: {
          left: ['vendor_name'],
        },
      }}
      sortingMode="server"
      sortModel={
        sort?.sortBy
          ? [
              {
                field: sort.sortBy,
                sort: sort.sortOrder === 'ASC' ? 'asc' : 'desc',
              },
            ]
          : []
      }
      onSortModelChange={handleSortModelChange}
      paginationMode="server"
      rowCount={data?.count || 0}
      paginationModel={{
        page: pagination.page - 1,
        pageSize: pagination.pageSize,
      }}
      onPaginationModelChange={handlePaginationModelChange}
      pageSizeOptions={[15]}
    />
  );
}

export default VendorApplianceDataGrid;
